/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback } from "react";

// antd:
import { Form } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
// import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getAppState, getTheme, getLng, getUser } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

const RZcanvas:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZcanvas.displayName = "Canvas";
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const lng = useAppSelector(getLng);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, align} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {color, backgroundColor, borders} = elState.attributes;
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState?.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!~userClss.indexOf("rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, borders, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor) {
      if (bclr) { est.backgroundColor = bclr }
      else { est.backgroundColor = (theme === "light" ? "#fff" : "#141414") }
    }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const canvasSize = useMemo<{width?:number, height?:number}>(() => {
    const parseCanvasSize = (value:any):number | undefined => {
      if (rz.isUndefinedOrNull(value)) { return undefined }
      if (typeof value === "number" && Number.isFinite(value) && value > 0) { return Math.round(value) }
      const strValue = String(value).trim();
      if (/^\d+(\.\d+)?(px)?$/i.test(strValue)) { return Math.round(parseFloat(strValue)) }
      return undefined;
    };

    return {
      width: parseCanvasSize(elState.attributes.width),
      height: parseCanvasSize(elState.attributes.height)
    };
  }, [elState.attributes.width, elState.attributes.height]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // if (iType.rules) { frmItemProps.name = String(key); frmItemProps.rules = iType.rules; frmItemProps.getValueProps = () => (elState.value) }
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  return wrapperForm(
    // eslint-disable-next-line react/no-unknown-property
    <canvas
      key={key}
      id={key}
      width={canvasSize.width}
      height={canvasSize.height}
      className={clsu.join(" ")}
      style={styl}
      uk-tooltip={tooltip}
    />
  );
}

export default RZcanvas;
/* eslint-enable @typescript-eslint/no-unused-vars */
